import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Propuestas} from '@/components/perfil-propuestas'
import {Tag} from '@/components/ui/tag'
import {OrigenLinea} from '@/components/origen-linea'
import {CabeceraDeBloque} from '@/components/perfil-bloque-cabecera'
import {CONFIRMADAS_AL_ENTRAR, type Apertura, type Bloque} from '@/lib/perfil-bloques'
import type {PerfilBloqueRead, PerfilLineaRead} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'

/** Lo propuesto va aparte de lo guardado: pintado igual se leería como un hecho que nadie confirmó.
 * Los botones se apagan con un guardado en vuelo: todos mandan el mismo `version_base`. */
export function PerfilBloque({
  bloque,
  businessId,
  editable,
  pending,
  desplegado,
  ayuda,
  onAdoptar,
  onAdoptarTodas,
  onQuitar,
  onConfirmarLinea,
  onEditar,
  onAgregar,
}: {
  bloque: PerfilBloqueRead
  businessId: string | undefined
  editable: boolean
  pending: boolean
  desplegado: boolean
  ayuda?: ReactNode
  onAdoptar: (linea: PerfilLineaRead) => void
  onAdoptarTodas: (lineas: PerfilLineaRead[]) => void
  onQuitar: (linea: PerfilLineaRead) => void
  onConfirmarLinea: (linea: PerfilLineaRead) => void
  /** Editar la línea en su sitio (KAI-146); sin él, el bloque es de solo lectura por línea. */
  onEditar?: (linea: PerfilLineaRead) => void
  onAgregar: (apertura: Apertura) => void
}) {
  const {t} = useTranslation()
  // En funciones no hay paso de confirmar una a una (KAI-146): ni chip ni botón.
  const confirmable = !CONFIRMADAS_AL_ENTRAR.has(bloque.bloque as Bloque)
  return (
    <section className="surface rounded-lg p-4" data-testid={`bloque-${bloque.bloque}`}>
      <CabeceraDeBloque
        bloque={bloque.bloque as Bloque}
        ayuda={ayuda}
        editable={editable}
        pending={pending}
        onAgregar={(abierto) => onAgregar({modo: 'nueva', bloque: abierto})}
      />

      {bloque.lineas.length === 0 && (
        <p className="mt-2 text-sm text-muted-foreground" data-testid={`bloque-vacio-${bloque.bloque}`}>
          {t('perfilCargo.detalle.sinLineas')}
        </p>
      )}
      <ul className="mt-2 space-y-3">
        {bloque.lineas.map((linea) => (
          <li key={linea.clave} className="space-y-1" data-testid={`linea-${linea.clave}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span className="whitespace-pre-wrap text-sm text-foreground">{linea.texto}</span>
              {confirmable &&
                (linea.confirmado ? (
                  <Tag tone="green">{t('perfilCargo.estado.confirmado')}</Tag>
                ) : (
                  <Tag tone="amber">{t('perfilCargo.estado.borrador')}</Tag>
                ))}
            </div>
            <OrigenLinea linea={linea} businessId={businessId} desplegado={desplegado} />
            {editable && (
              <div className="flex flex-wrap gap-2">
                {confirmable && !linea.confirmado && (
                  <Button size="sm" variant="outline" disabled={pending} onClick={() => onConfirmarLinea(linea)}>
                    {t('perfilCargo.detalle.confirmarLinea')}
                  </Button>
                )}
                <Button size="sm" variant="ghost" disabled={pending} onClick={() => onQuitar(linea)}>
                  {t('perfilCargo.detalle.quitar')}
                </Button>
                {onEditar && (
                  <Button size="sm" variant="ghost" disabled={pending} onClick={() => onEditar(linea)}>
                    {t('perfilCargo.tabla.editar')}
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      <Propuestas
        bloque={bloque.bloque}
        propuestas={bloque.propuestas}
        editable={editable}
        pending={pending}
        onAdoptarTodas={onAdoptarTodas}>
        <ul className="mt-1 space-y-3">
          {bloque.propuestas.map((linea) => (
            <li key={linea.clave} className="space-y-1" data-testid={`propuesta-${linea.clave}`}>
              <p className="whitespace-pre-wrap text-sm text-foreground">{linea.texto}</p>
              <OrigenLinea linea={linea} businessId={businessId} desplegado={desplegado} />
              {editable && (
                <Button size="sm" variant="outline" disabled={pending} onClick={() => onAdoptar(linea)}>
                  {t('perfilCargo.detalle.adoptar')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Propuestas>
    </section>
  )
}
